import type {
  GridApi,
  IDatasource,
  IGetRowsParams,
  PaginationChangedEvent,
} from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NormalizedMemberData } from "./utils";

import { fetchOrganizationMembers } from "@/queries/organization-members";
import { useDebounce } from "@/hooks/debounce";
import { useActiveOrganization } from "@/hooks/organizations";
import { useUserToken } from "@/context/auth-context";
import { displayErrorToast } from "@/utils/toast";

const DEFAULT_PAGE_SIZE = 50;

export const useOrganizationMembersData = (organizationId?: number) => {
  const [search, setSearch] = useState("");
  const [workspaceIds, setWorkspaceIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [activeRequestCount, setActiveRequestCount] = useState(0);
  const gridApiRef = useRef<GridApi | null>(null);

  const userToken = useUserToken();
  const { data: activeOrganization } = useActiveOrganization();
  const effectiveOrgId = organizationId ?? activeOrganization?.id;

  const debouncedSearch = useDebounce(search, 400);
  const searchRef = useRef(debouncedSearch);
  searchRef.current = debouncedSearch;
  const workspaceIdsRef = useRef(workspaceIds);
  workspaceIdsRef.current = workspaceIds;

  const datasource = useMemo<IDatasource | undefined>(() => {
    if (!userToken || !effectiveOrgId) {
      return undefined;
    }

    return {
      getRows: async (params: IGetRowsParams) => {
        const requestedPageSize = Math.max(pageSize, params.endRow - params.startRow || pageSize);
        const requestedPage = Math.floor(params.startRow / requestedPageSize) + 1;

        setActiveRequestCount(c => c + 1);
        try {
          const response = await fetchOrganizationMembers({
            userToken,
            organizationId: effectiveOrgId,
            page: requestedPage,
            perPage: requestedPageSize,
            search: searchRef.current.trim() || undefined,
            workspaceIds: workspaceIdsRef.current.length ? workspaceIdsRef.current : undefined,
          });

          params.successCallback(response.items as NormalizedMemberData[], response.total);
        } catch {
          params.failCallback();
          displayErrorToast("Error loading members");
        } finally {
          setActiveRequestCount(c => Math.max(0, c - 1));
        }
      },
    };
  }, [effectiveOrgId, pageSize, userToken]);

  useEffect(() => {
    gridApiRef.current?.refreshInfiniteCache();
  }, [debouncedSearch, workspaceIds]);

  const setGridApiRef = useCallback((api: GridApi) => {
    gridApiRef.current = api;
  }, []);

  const handlePaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      const { api } = event;
      const newPage = api.paginationGetCurrentPage() + 1;
      const newPageSize = api.paginationGetPageSize();

      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
        return;
      }
      if (newPage !== page) {
        setPage(newPage);
      }
    },
    [page, pageSize],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleWorkspaceIdsChange = useCallback((value: number[]) => {
    setWorkspaceIds(value);
    setPage(1);
  }, []);

  const isTableLoading = activeRequestCount > 0;

  const refreshGrid = useCallback(() => {
    gridApiRef.current?.refreshInfiniteCache();
  }, []);

  return {
    datasource,
    setGridApiRef,
    currentPage: page,
    pageSize,
    handlePaginationChanged,
    search,
    setSearch: handleSearchChange,
    workspaceIds,
    setWorkspaceIds: handleWorkspaceIdsChange,
    isLoading: isTableLoading,
    refreshGrid,
  };
};
